import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createGlobalStyle } from 'styled-components';
import indiageojson from '../resources/in.json'; // Local copy of GeoJSON data
// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const GlobalMapStyles = createGlobalStyle`
  .leaflet-container {
    background: #fff;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }

  .blurred-tiles {
    filter: blur(3px) grayscale(100%);
    opacity: 0;
  }
  .leaflet-pane,
  .leaflet-overlay-pane,
  .leaflet-map-pane {
    filter: none !important;
  }

  
  .info-tooltip {
    background-color: rgba(255, 255, 255, 0.95);
    border: none;
    border-radius: 2px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    padding: 12px 16px;
    min-width: 120px;
  }
  
  .info-tooltip h4 {
    margin: 0 0 8px;
    color: #333;
    font-size: 16px;
    font-weight: 600;
  }
  
  .info-tooltip .count {
    color: #333;
    font-weight: 600;
    font-size: 14px;
  }
  
  .legend {
    background-color: rgba(255, 255, 255, 0.95);
    border: none;
    border-radius: 2px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    padding: 12px 16px;
    line-height: 20px;
    font-size: 12px;
  }
  
  .legend h4 {
    margin: 0 0 10px;
    color: #333;
    font-size: 14px;
    font-weight: 600;
  }
  
  .legend i {
    width: 18px;
    height: 18px;
    float: left;
    margin-right: 8px;
    opacity: 0.8;
    border-radius: 2px;
  }
  
  .legend-item {
    margin-bottom: 4px;
    clear: both;
    overflow: hidden;
  }

  .leaflet-popup-content-wrapper {
    border-radius: 2px;
  }

  .leaflet-popup-content {
    margin: 8px 12px;
    font-size: 14px;
  }

  .state-label {
    background: transparent;
    border: none;
    box-shadow: none;
    font-weight: 600;
    color: "#666";
    text-shadow: 
      -1px -1px 0 rgba(255, 255, 255, 0.8),  
       1px -1px 0 rgba(255, 255, 255, 0.8),
      -1px  1px 0 rgba(255, 255, 255, 0.8),
       1px  1px 0 rgba(255, 255, 255, 0.8);

    font-size: 10px;
    text-align: center;
    white-space: nowrap;
  }
`;

const getColor = (d) => {
  return d > 100000 ? '#4A3470' :
    d > 50000 ? '#7957B2' :
      d > 10000 ? '#9479C1' :
        d > 5000 ? '#A58FCB' :
          d > 1000 ? '#DED1F5' :
            d > 500 ? '#E1DAEE' : '#e6e0ecff';



};

const getIntensity = (count) => {
  if (count > 100000) return 'Very High';
  if (count > 50000) return 'High';
  if (count > 10000) return 'Moderate-High';
  if (count > 5000) return 'Moderate';
  if (count > 1000) return 'Low-Moderate';
  if (count > 500) return 'Low';
  if (count > 50) return 'Very Low';
  return 'Minimal';
};

const IndiaMap = ({ data = {}, title = "Complaint Distribution by State" }) => {
  const mapRef = useRef(null);
  const geojsonRef = useRef(null);
  const infoRef = useRef(null);
  const legendRef = useRef(null);

  // format of state names in data for better matching

  useEffect(() => {
    const initMap = async () => {
      try {
        // Process the data - normalize state names for better matching
        const processedData = {};
        const stateNameMapping = {
          'Andaman and Nicobar': ['ANDAMAN AND NICOBAR ISLANDS', 'ANDAMAN NICOBAR', 'ANDAMAN & NICOBAR', 'ANDAMAN AND NICOBAR'],
          'Andhra Pradesh': ['ANDHRA PRADESH', 'AP'],
          'Arunachal Pradesh': ['ARUNACHAL PRADESH'],
          'Assam': ['ASSAM'],
          'Bihar': ['BIHAR'],
          'Chandigarh': ['CHANDIGARH'],
          'Chhattisgarh': ['CHHATTISGARH', 'CHATTISGARH'],
          'Dādra and Nagar Haveli and Damān and Diu': ['DADRA AND NAGAR HAVELI', 'DADRA & NAGAR HAVELI', 'DADRA AND NAGAR HAVELI AND DAMAN AND DIU', 'DAMAN AND DIU', 'DAMAN & DIU'],
          'Delhi': ['DELHI', 'NEW DELHI', 'NCT OF DELHI'],
          'Goa': ['GOA'],
          'Gujarat': ['GUJARAT'],
          'Haryana': ['HARYANA'],
          'Himachal Pradesh': ['HIMACHAL PRADESH', 'HP'],
          'Jammu and Kashmir': ['JAMMU AND KASHMIR', 'J&K', 'JAMMU & KASHMIR'],
          'Jharkhand': ['JHARKHAND'],
          'Karnataka': ['KARNATAKA'],
          'Kerala': ['KERALA'],
          'Ladakh': ['LADAKH'],
          'Lakshadweep': ['LAKSHADWEEP'],
          'Madhya Pradesh': ['MADHYA PRADESH', 'MP'],
          'Maharashtra': ['MAHARASHTRA'],
          'Manipur': ['MANIPUR'],
          'Meghalaya': ['MEGHALAYA'],
          'Mizoram': ['MIZORAM'],
          'Nagaland': ['NAGALAND'],
          'Orissa': ['ODISHA', 'ORISSA'],
          'Puducherry': ['PUDUCHERRY', 'PONDICHERRY'],
          'Punjab': ['PUNJAB'],
          'Rajasthan': ['RAJASTHAN'],
          'Sikkim': ['SIKKIM'],
          'Tamil Nadu': ['TAMIL NADU', 'TAMILNADU'],
          'Telangana': ['TELANGANA'],
          'Tripura': ['TRIPURA'],
          'Uttaranchal': ['UTTARAKHAND', 'UTTARANCHAL', 'UTTRAKHAND'],
          'Uttar Pradesh': ['UTTAR PRADESH', 'UP'],
          'West Bengal': ['WEST BENGAL']
        };

        // Normalize and map data
        if (data && typeof data === 'object') {
          Object.entries(data).forEach(([inputState, count]) => {
            const normalizedInput = inputState.toUpperCase().trim();

            // Find matching canonical state name
            let matched = false;
            for (const [canonical, variations] of Object.entries(stateNameMapping)) {
              if (variations.some(variant => variant === normalizedInput)) {
                processedData[canonical] = (processedData[canonical] || 0) + (parseInt(count) || 0);
                matched = true;
                break;
              }
            }

            // Fallback - use direct mapping if no match found
            if (!matched) {
              processedData[normalizedInput] = parseInt(count) || 0;
            }
          });
        }

        // Update info function
        const updateInfo = (feature) => {
          if (!infoRef.current) return;

          if (feature) {
            const stateName = feature.properties.name;
            const count = processedData[stateName] || 0;
            const intensity = getIntensity(count);

            infoRef.current.innerHTML = `
              <h4>${feature.properties.name}</h4>
              <div class="count">${count.toLocaleString()} complaints</div>
              <div style="color: #666; font-size: 12px; margin-top: 4px;">${intensity}</div>
            `;
          } else {
            infoRef.current.innerHTML = '<h4>Hover over a state</h4><div style="color: #666;">View complaint data</div>';
          }
        };

        // Initialize map if not already done
        if (!mapRef.current) {
          mapRef.current = L.map('map', {
            center: [23.5937, 90.3629],
            zoom: 10,           // Changed from 8 to 5 for initial zoom
            minZoom: 4.5,        // Changed from 4 to 3 to allow more zooming out
            maxZoom: 12,       // Changed from 10 to 12 to allow more zooming in
            zoomControl: true,
            attributionControl: false
          });

          // Add tile layer with blur effect
          // Add tile layer with English labels (CartoDB Voyager)
          // L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          //   attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          //   subdomains: 'abcd',
          //   maxZoom: 20
          // }).addTo(mapRef.current);

          // Add info control
          const info = L.control({ position: 'topright' });
          info.onAdd = () => {
            const div = L.DomUtil.create('div', 'info-tooltip');
            infoRef.current = div;
            updateInfo();
            return div;
          };
          info.addTo(mapRef.current);

          // Add legend control
          const legend = L.control({ position: 'bottomright' });
          legend.onAdd = () => {
            const div = L.DomUtil.create('div', 'legend');
            legendRef.current = div;

            const grades = [0, 500, 1000, 5000, 10000, 50000, 100000];
            const labels = ['0-500', '501-1K', '1K-5K', '5K-10K', '10K-50K', '50K-100K', '100K+'];


            div.innerHTML = `<h4>${title.split(' ').slice(0, 2).join(' ')}</h4>`;

            for (let i = 0; i < grades.length; i++) {
              div.innerHTML += `
                <div class="legend-item">
                  <i style="background:${getColor(grades[i] + 1)}"></i>
                  ${labels[i]}
                </div>
              `;
            }
            return div;
          };
          legend.addTo(mapRef.current);
        }

        // Style function for states
        const style = (feature) => {
          const stateName = feature.properties.name;
          const count = processedData[stateName] || 0;
          const color = getColor(count);


          return {
            fillColor: color,
            weight: 2,
            opacity: 1,
            color: '#666',
            dashArray: '',
            fillOpacity: 0.7
          };
        };

        // Event handlers
        const highlightFeature = (e) => {
          const layer = e.target;

          layer.setStyle({
            weight: 3,
            color: '#333',
            dashArray: '',
            fillOpacity: 0.9
          });

          if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
            layer.bringToFront();
          }

          updateInfo(layer.feature);
        };

        const resetHighlight = (e) => {
          if (geojsonRef.current) {
            geojsonRef.current.resetStyle(e.target);
          }
          updateInfo();
        };

        const zoomToFeature = (e) => {
          const layer = e.target;
          const feature = layer.feature;
          const stateName = feature.properties.name;
          const count = processedData[stateName] || 0;

          // Show popup with detailed info
          const popupContent = `
            <div style="text-align: center;">
              <h3 style="margin: 0 0 8px; color: #333;">${stateName}</h3>
              <div style="font-size: 18px; font-weight: bold; color: #333;">
                ${count.toLocaleString()} complaints
              </div>
              <div style="color: #666; font-size: 12px; margin-top: 4px;">
                Intensity: ${getIntensity(count)}
              </div>
            </div>
          `;

          layer.bindPopup(popupContent).openPopup();
          mapRef.current.fitBounds(layer.getBounds(), { padding: [20, 20] });
        };

        // Remove existing layer
        if (geojsonRef.current) {
          mapRef.current.removeLayer(geojsonRef.current);
        }

        // Fetch GeoJSON data from GitHub
        // const response = await fetch('https://raw.githubusercontent.com/Subhash9325/GeoJson-Data-of-Indian-States/refs/heads/master/Indian_States');
        const response = { ok: true, json: async () => indiageojson }; // Using local copy

        if (!response.ok) {
          throw new Error(`Failed to fetch GeoJSON data: ${response.status}`);
        }

        const geojsonData = await response.json();

        // Add GeoJSON layer
        geojsonRef.current = L.geoJSON(geojsonData, {
          style: style,
          onEachFeature: (feature, layer) => {
            layer.on({
              mouseover: highlightFeature,
              mouseout: resetHighlight,
              click: zoomToFeature
            });

            if (feature.properties && feature.properties.name) {
              layer.bindTooltip(feature.properties.name, {
                permanent: true,
                direction: 'center',
                className: 'state-label',
                opacity: 1
              });
            }
          }
        }).addTo(mapRef.current);

        // Fit map to show all of India
        mapRef.current.fitBounds(geojsonRef.current.getBounds(), {
          padding: [10, 10]
        });

      } catch (error) {
        console.error('Error initializing map:', error);

        // Show error message in info panel
        if (infoRef.current) {
          infoRef.current.innerHTML = `
            <h4 style="color: red;">Error Loading Map</h4>
            <div style="font-size: 12px; color: #666;">
              Failed to load geographic data. Please check your internet connection.
            </div>
          `;
        }
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        geojsonRef.current = null;
        infoRef.current = null;
        legendRef.current = null;
      }
    };
  }, [data, title]);

  return (
    <>
      <GlobalMapStyles />
      <div
        id="map"
        style={{
          height: '800px',
          width: '100%',
          borderRadius: '2px',
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      />
    </>
  );
};

export default IndiaMap;